import { z } from 'zod';
import { ColorSchema, ColorsSchema, ConditionSchema, LOOK, NameSchema, SourceRefSchema, about, numberFormat, part, sourceOf, transform } from '../schema_fields.ts';
import { TileStyleSchema } from '../tile_style.ts';
import { IN_GAUGE_COLOURS, gaugeColourFields, gaugeLookField } from './gauge_look.ts';
// The value tile: a number with its unit, optionally on a gauge and with a sparkline below or behind it.

export const UNIT_POSITIONS = ['right-top', 'right-center', 'right-bottom', 'below'] as const;
export type UnitPosition = (typeof UNIT_POSITIONS)[number];

export const SPARKLINE_PLACEMENTS = ['below', 'behind'] as const;
export type SparklinePlacement = (typeof SPARKLINE_PLACEMENTS)[number];

/**
 * A value tile's sparkline (tiles spec §3, issue #13): one source's readings as a line below the
 * value, scaled to them. Its source left out: the value's own (SP-4).
 */
export const SparklineSchema = z.object({
    source: sourceOf(NameSchema.optional(), { gives: ['history', 'number'], label: 'Readings', whenUnset: 'The value’s' }, 'the value’s own when left out'),
    color: ColorSchema.default('#ffffff').meta({ ...about('Colour', undefined, 'color'), ...LOOK }),
    /** Device pixels, as the graph's line was (SP-7). */
    lineWidth: z.number().positive().default(1).meta({ ...about('Line width', 'in pixels'), ...LOOK }),
    /** The area below the line in its colour, half opaque at the highest reading, clear at the bottom (SP-10). */
    fill: z.boolean().default(false).meta({ ...about('Fill', 'the area below the line, fading out to the bottom'), ...LOOK }),
    /** In percent of the tile's content area (SP-12, issue #29): 20 below, 100 behind when left out. */
    height: z.number().min(5).max(100).optional().meta({ ...about('Height', 'in percent of the tile: 20 below the value, 100 behind it when left out'), ...LOOK }),
    /** In a strip below the value, or across the whole tile behind it (SP-11). */
    placement: z.enum(SPARKLINE_PLACEMENTS).default('below').meta({
        ...about('Placement', 'in a strip below the value, or across the whole tile behind it'),
        ...LOOK,
        'x-labels': { below: 'Below the value', behind: 'Behind the value' },
    }),
});

/**
 * A style rule (dashboard-editor spec §5.10): while `when` holds for the value the tile shows, `style`
 * applies over the tile's own, any of its properties. Every rule that holds applies, top to bottom.
 */
export const StyleRuleSchema = z.object({
    when: ConditionSchema.meta(about('When', 'a condition on the value, such as value > 10 or value >= 5 and value < 10', 'condition')),
    style: TileStyleSchema.default({}).meta(about('Style', 'what changes while it holds', 'tile-style')),
    /** The caption while it holds, over the tile's (SR-8). */
    caption: z.string().optional().meta(about('Caption', 'the text below the value while it holds, such as Comfortable')),
}).meta({ title: 'Rule' });

export const ValueOptionsSchema = z.object({
    // A list too: the value is its last reading (SP-5).
    source: sourceOf(SourceRefSchema, { gives: ['number', 'level', 'history'], label: 'Reading' }),
    numberFormat: numberFormat('0'),
    transform: transform(),
    color: ColorSchema.optional().meta({ ...about('Colour', 'of the number', 'color'), ...LOOK }),
    /** Text only, shown after the number, or below it on a gauge (tiles spec TU-1). */
    unit: z.string().default('').meta(about('Unit', 'shown after the number, such as °C')),
    /** The unit's size in percent of its usual size (tiles spec TU-5, issue #6). */
    unitSize: z.number().min(25).max(400).default(100).meta({ ...about('Unit size', 'in percent: 100 as usual, 200 double'), ...LOOK }),
    /** Where the unit goes beside the number (tiles spec TU-6, issue #25); a gauge places it itself. */
    unitPosition: z.enum(UNIT_POSITIONS).default('right-top').meta({
        ...about('Unit position', 'beside the number; a gauge has it below the number'),
        ...LOOK,
        'x-labels': { 'right-top': 'Right, at the top', 'right-center': 'Right, in the middle', 'right-bottom': 'Right, at the bottom', 'below': 'Below the number' },
    }),
    /** A line of text below the value (SR-8), in the text colour; a style rule's over it. */
    caption: z.string().optional().meta(about('Caption', 'a line of text below the value; style rules can change it')),
    gauge: z.object({
        min: z.number().default(0).meta(about('Min')),
        max: z.number().meta(about('Max')),
        colors: ColorsSchema.default('red-green').meta({ ...about('Gradient', 'a named one such as red-green, or a list of colours', 'colors'), ...IN_GAUGE_COLOURS, ...LOOK }),
        ...gaugeColourFields,
        icon: z.string().optional().meta(about('Icon', 'in the middle', 'icon')),
        iconColor: ColorSchema.optional().meta({ ...about('Icon colour', undefined, 'color'), ...LOOK }),
        secondary: z.object({
            source: sourceOf(NameSchema, { gives: ['number', 'level'], label: 'Reading' }),
            numberFormat: numberFormat('0'),
            unit: z.string().default('').meta(about('Unit', 'shown after it')),
            color: ColorSchema.optional().meta(about('Colour', undefined, 'color')),
        })
        .optional()
        .meta(about('Second value', 'a smaller value below the first')),
        /**
         * Values beyond the first two, each a ring of its own in the rings style (the owner: "the
         * capability of the gauge component to have multiple values should be added"); on the gauge's
         * scale unless they set their own.
         */
        more: z.array(z.object({
            source: sourceOf(NameSchema, { gives: ['number', 'level'], label: 'Reading' }),
            numberFormat: numberFormat('0'),
            unit: z.string().default('').meta(about('Unit', 'shown after it')),
            color: ColorSchema.optional().meta(about('Colour', undefined, 'color')),
            min: z.number().optional().meta(about('Min', 'the gauge\'s when left out')),
            max: z.number().optional().meta(about('Max', 'the gauge\'s when left out')),
        }).meta({ title: 'Value' }))
        .default([])
        .meta(about('More values', 'for the rings style: a ring and a number each')),
        look: gaugeLookField,
    })
    .optional()
    // A style may add one, with its range or else 0 to 100 (tile-styles.md TY-2).
    .meta({ ...about('Gauge', 'the value on a dial'), ...part({ min: 0, max: 100 }) }),
    sparkline: SparklineSchema.optional().meta({ ...about('Sparkline', 'readings over time as a line below or behind the value'), ...part({}) }),
    styleRules: z.array(StyleRuleSchema).default([]).meta(about('Style rules', 'top to bottom; each that holds for the value changes the style, a later one over an earlier one')),
});
